import { DEFAULT_PRESET, WAVE_PRESETS } from "./presets";
import { probePixel } from "./probe";
import { type RenderState, TopoRenderer } from "./renderer";
import type {
  ContourOptions,
  CursorOptions,
  ProbeResult,
  SargassoController,
  SargassoOptions,
  WaveParams,
} from "./types";

export {
  amplitudeSum,
  angularFrequency,
  gerstnerGradient,
  gerstnerHeight,
  gerstnerNormal,
  waveNumber,
  wavePhase,
} from "./gerstner";
export {
  DEFAULT_PRESET,
  generateWaveSpectrum,
  type SpectrumOptions,
  WAVE_PRESET_NAMES,
  WAVE_PRESETS,
  type WavePreset,
} from "./presets";
export { pixelToWorld, probePixel, probeWorld } from "./probe";
export { MAX_WAVES } from "./shaders/topo.frag";
export * from "./types";

/**
 * Default contour lines: a light intermediate line every 0.5 world units, with a
 * bolder index line every 5th (like a topographic map).
 */
export const DEFAULT_CONTOUR: ContourOptions = {
  interval: 0.5,
  width: 1.2,
  color: [0.82, 0.88, 0.98, 0.55],
  emphasisEvery: 5,
  emphasisWidth: 3,
  emphasisColor: [0.9, 0.94, 1.0, 0.9],
};

/**
 * The default wave set (the {@link DEFAULT_PRESET} preset) — a rich, realistic
 * spectrum used when no waves are supplied. Copy before mutating.
 */
export const DEFAULT_WAVES: WaveParams[] = WAVE_PRESETS[DEFAULT_PRESET]?.waves ?? [];

interface ResolvedOptions {
  waves: WaveParams[];
  contour: ContourOptions | null;
  extent: number;
  speed: number;
  steepness: number;
  background: [number, number, number, number];
  dpr: number;
  autoplay: boolean;
}

function defaultDpr(): number {
  return typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1;
}

function resolve(base: ResolvedOptions, patch: Partial<SargassoOptions>): ResolvedOptions {
  return {
    waves: patch.waves ?? base.waves,
    contour: patch.contour === undefined ? base.contour : patch.contour,
    extent: patch.extent ?? base.extent,
    speed: patch.speed ?? base.speed,
    steepness: patch.steepness ?? base.steepness,
    background: patch.background ?? base.background,
    dpr: patch.dpr ?? base.dpr,
    autoplay: patch.autoplay ?? base.autoplay,
  };
}

/**
 * Create a Sargasso visualization bound to a canvas.
 *
 * Renders an animated top-down topographic contour map of a Gerstner-wave
 * height field — contour lines only, no color fill. The returned controller
 * lets you update parameters and sample the exact wave height under a pixel.
 */
export function createSargasso(
  canvas: HTMLCanvasElement,
  options: SargassoOptions,
): SargassoController {
  let opts: ResolvedOptions = resolve(
    {
      waves: DEFAULT_WAVES,
      contour: DEFAULT_CONTOUR,
      extent: 20,
      speed: 1,
      steepness: 0,
      background: [0, 0, 0, 0],
      dpr: defaultDpr(),
      autoplay: true,
    },
    options,
  );

  const renderer = new TopoRenderer(canvas);
  let simTime = 0;
  let lastTs: number | null = null;
  let rafId: number | null = null;
  let observer: ResizeObserver | null = null;

  function probeAt(pos: CursorOptions): ProbeResult {
    const dpr = opts.dpr;
    return probePixel(
      opts.waves,
      pos.x * dpr,
      pos.y * dpr,
      canvas.width,
      canvas.height,
      opts.extent,
      simTime,
      opts.steepness,
    );
  }

  function buildRenderState(): RenderState {
    return {
      waves: opts.waves,
      extent: opts.extent,
      timeSeconds: simTime,
      contour: opts.contour,
      background: opts.background,
      steepness: opts.steepness,
    };
  }

  function draw(): void {
    renderer.render(buildRenderState());
  }

  function resize(): void {
    const dpr = opts.dpr;
    const cssW = canvas.clientWidth || canvas.width || 300;
    const cssH = canvas.clientHeight || canvas.height || 150;
    renderer.setSize(cssW * dpr, cssH * dpr);
    draw();
  }

  const frame = (ts: number): void => {
    if (lastTs === null) lastTs = ts;
    const dt = Math.min((ts - lastTs) / 1000, 0.1);
    lastTs = ts;
    simTime += dt * opts.speed;
    draw();
    rafId = requestAnimationFrame(frame);
  };

  function start(): void {
    if (rafId !== null) return;
    if (typeof requestAnimationFrame !== "function") {
      draw();
      return;
    }
    lastTs = null;
    rafId = requestAnimationFrame(frame);
  }

  function stop(): void {
    if (rafId !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(rafId);
    }
    rafId = null;
  }

  // Initial sizing + optional live resize handling.
  resize();
  if (typeof ResizeObserver === "function") {
    observer = new ResizeObserver(() => resize());
    observer.observe(canvas);
  }

  if (opts.autoplay) start();
  else draw();

  return {
    supported: renderer.supported,
    setOptions(patch: Partial<SargassoOptions>): void {
      const prevDpr = opts.dpr;
      opts = resolve(opts, patch);
      if (opts.dpr !== prevDpr) resize();
      else draw();
    },
    getOptions() {
      return { ...opts };
    },
    probe(pos: CursorOptions): ProbeResult {
      return probeAt(pos);
    },
    resize,
    start,
    stop,
    renderFrame: draw,
    destroy(): void {
      stop();
      observer?.disconnect();
      observer = null;
      renderer.destroy();
    },
  };
}
